﻿import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringMirrorObject } from "../../objects/StringMirrorObject";
import { StringObject } from "../../objects/StringObject";
import { EasingFunctionOutput } from "../../tools/EasingFunctionTool";
import { styleTxn } from "../../utils/style-txn";

export class StringProgress extends StringModule {
  protected updateScheduled = false;
  private static readonly PROGRESS_EPSILON = 1e-4;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "progress";

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "easing", type: "easing", fallback: this.settings["easing"] },
    ];
  }

  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
  }

  private recomputeProgress(object: StringObject): void {
    const startPosition = object.getProperty<number>("start-position") ?? 0;
    const differencePosition = object.getProperty<number>("difference-position") ?? 0;

    let rawProgress = 0;
    rawProgress = Math.min(
      1,
      Math.max(0, (this.data.scroll.transformedCurrent - startPosition) / differencePosition)
    );

    object.setProperty<number>("progress-raw", rawProgress);

    const easing = object.getProperty<EasingFunctionOutput>("easing");
    const progress = typeof easing === "function" ? easing(rawProgress) : rawProgress;
    this.applyProgressValue(object, progress);
  }

  private applyProgressValue(object: StringObject, progress: number): void {
    object.setProperty<number>("progress", progress);
  }

  calculatePositions(object: StringObject, windowSize: number) {
    super.calculatePositions(object, windowSize);
    this.recomputeProgress(object);
  }

  onScroll(data: StringData): void {
    super.onScroll(data);
  }

  onObjectConnected(object: StringObject) {
    super.onObjectConnected(object);
  }

  onScrollMeasure(data: StringData) {
    this.objects.forEach((object) => {
      this.recomputeProgress(object);
    });
  }

  onMutate() {
    this.objects.forEach((object) => {
      this.updateObjectProgress(object);
    });
  }

  private updateObjectProgress(object: StringObject): void {
    const key = object.getProperty<string>("key");
    const progress = object.getProperty<number>("progress");

    if (typeof progress !== "number" || !Number.isFinite(progress)) {
      return;
    }
    const rawProgress = object.getProperty<number>("progress-raw") ?? progress;
    const progressStr = progress.toString();
    const parentEasing = object.getProperty<EasingFunctionOutput>("easing");
    const lastEmittedProgress = object.getProperty<number>("progress-emitted");

    if (!this.hasProgressChanged(lastEmittedProgress, progress)) {
      return;
    }

    object.setProperty<number>("progress-emitted", progress);

    this.events.emit(`object:progress:${object.id}`, progress);

    this.applyToElementAndConnects(
      object,
      (el) => {
        if (key) {
          styleTxn.setVars(el, { [key]: progressStr });
        }
      },
      (el: HTMLElement, mirror?: StringMirrorObject) => {
        if (!mirror) return;
        const eased = mirror.applyProgress(
          rawProgress,
          typeof parentEasing === "function" ? parentEasing : undefined
        );
        mirror.setProperty("progress", eased);
        if (key) {
          styleTxn.setVars(el, { [key]: eased.toString() });
        }
      }
    );
  }

  private hasProgressChanged(previous: number | null, next: number): boolean {
    if (previous === null) {
      return true;
    }
    return Math.abs(previous - next) > StringProgress.PROGRESS_EPSILON;
  }

  onObjectDisconnected(object: StringObject): void {
    super.onObjectDisconnected(object);
    const key = object.getProperty<string>("key");
    if (!key) return;

    const clearCustomProperty = (el: HTMLElement) => {
      el.style.removeProperty(key);
    };

    clearCustomProperty(object.htmlElement);
    object.mirrorObjects.forEach((mirror) => {
      clearCustomProperty(mirror.htmlElement);
    });
  }
}
